import { useState, useEffect } from 'react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useLanguage } from '../../i18n'

interface SidebarProps {
  tenantName: string
  tenantSlug?: string
  tenantLogoUrl?: string
  tenants?: Array<{ id: string; slug: string; name: string }>
  onTenantChange?: (slug: string) => void
  activeSection?: string
  onSectionChange?: (section: string) => void
  threatCount?: number
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  socradarEnabled?: boolean
  hasMitreData?: boolean
}

export default function Sidebar({
  tenantName,
  tenantSlug: _tenantSlug,
  tenantLogoUrl,
  tenants = [],
  onTenantChange,
  activeSection = 'dashboard',
  onSectionChange,
  threatCount = 0,
  isCollapsed = false,
  onToggleCollapse,
  socradarEnabled = false,
  hasMitreData = true
}: SidebarProps) {
  const { user } = useUser()
  const { signOut } = useClerk()
  const { t } = useLanguage()
  const [showTenantDropdown, setShowTenantDropdown] = useState(false)
  const [logoError, setLogoError] = useState(false)

  useEffect(() => { setLogoError(false) }, [tenantLogoUrl])

  // Tenants with socradar_enabled use direct SocRadar sections; others use Cyber Risk Report

  const navItems = {
    principal: [
      { id: 'dashboard', label: t.dashboard, icon: DashboardIcon },
      { id: 'threats', label: 'Alerts', icon: ShieldIcon, badge: threatCount > 0 ? threatCount : undefined },
      { id: 'users', label: t.users, icon: UsersIcon },
      { id: 'devices', label: t.devices, icon: DevicesIcon },
    ],
    seguridad: [
      { id: 'vulnerabilities', label: t.vulnerabilities, icon: VulnIcon },
      { id: 'mfa', label: t.mfaAccess, icon: LockIcon },
      { id: 'compliance', label: t.compliance, icon: DocumentIcon },
      { id: 'software', label: 'Software', icon: PackageIcon },
    ],
    analytics: [
      ...(hasMitreData ? [{ id: 'mitre', label: 'MITRE ATT&CK', icon: GridIcon }] : []),
      { id: 'threat-intel', label: 'Threat Intel', icon: ThreatIntelIcon },
      // SocRadar-enabled tenants get Attack Surface + 3 direct sections
      ...(socradarEnabled ? [
        { id: 'exposure', label: 'Attack Surface', icon: ExposureIcon },
        { id: 'socradar-incidents', label: 'Brand Protection', icon: BrandProtectionIcon },
        { id: 'socradar-asm', label: 'Digital Footprint', icon: FingerprintIcon },
        { id: 'dark-web', label: 'Dark Web', icon: DarkWebIcon },
      ] : []),
      { id: 'extensions', label: 'Extensions', icon: PuzzleIcon },
      { id: 'usb', label: 'USB Devices', icon: UsbIcon },
      { id: 'access', label: 'Access Map', icon: AccessIcon },
    ],
    reportes: [
      { id: 'export', label: t.export, icon: DownloadIcon },
      { id: 'history', label: t.history, icon: HistoryIcon },
    ],
    // Sprint 4: tenant-facing account section — currently only the
    // billing portal lives here, but this is the group for future
    // self-service stuff (contact info, contract docs, etc.)
    cuenta: [
      { id: 'branding', label: t.branding, icon: PaletteIcon },
      { id: 'billing', label: t.billing, icon: BillingIcon },
    ],
  }

  const handleNavClick = (sectionId: string) => {
    onSectionChange?.(sectionId)
  }

  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.primaryEmailAddress?.emailAddress?.slice(0, 2).toUpperCase() || 'JL'

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Collapse Toggle Button */}
      <button
        className="sidebar-toggle"
        onClick={onToggleCollapse}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          {isCollapsed ? (
            <path d="m9 18 6-6-6-6"/>
          ) : (
            <path d="m15 18-6-6 6-6"/>
          )}
        </svg>
      </button>

      {/* Logo — Jarix isotype (flame mark) */}
      <div className="logo-section">
        <img
          src="/branding/jarix-iso.png"
          alt="Jarix"
          className="logo-iso"
          width={40}
          height={40}
        />
        {!isCollapsed && (
          <div className="brand">
            <span className="brand-name">Jarix Hub</span>
            <span className="brand-subtitle">Security Portal</span>
          </div>
        )}
      </div>

      {/* Tenant Selector */}
      {!isCollapsed && (
        <>
          <div
            className="client-selector"
            onClick={() => tenants.length > 1 && setShowTenantDropdown(!showTenantDropdown)}
          >
            {tenantLogoUrl && !logoError ? (
              <img
                key={tenantLogoUrl}
                src={tenantLogoUrl}
                alt=""
                className="client-selector-logo"
                onError={() => setLogoError(true)}
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="client-selector-initial">{tenantName.charAt(0).toUpperCase()}</span>
            )}
            <span className="client-selector-name">{tenantName}</span>
            {tenants.length > 1 && (
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            )}
          </div>

          {showTenantDropdown && tenants.length > 1 && (
            <div className="tenant-dropdown">
              {tenants.map(t => (
                <div
                  key={t.id}
                  className={`tenant-option ${t.slug === tenantName ? 'active' : ''}`}
                  onClick={() => {
                    onTenantChange?.(t.slug)
                    setShowTenantDropdown(false)
                  }}
                >
                  {t.name}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Navigation */}
      <nav className="nav-section">
        {!isCollapsed && <div className="nav-label">{t.principal}</div>}
        {navItems.principal.map(item => (
          <div
            key={item.id}
            className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item.id)}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon />
            {!isCollapsed && item.label}
            {!isCollapsed && item.badge !== undefined && <span className="nav-badge">{item.badge}</span>}
            {isCollapsed && item.badge !== undefined && <span className="nav-badge-collapsed">{item.badge}</span>}
          </div>
        ))}
      </nav>

      <nav className="nav-section">
        {!isCollapsed && <div className="nav-label">{t.security}</div>}
        {navItems.seguridad.map(item => (
          <div
            key={item.id}
            className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item.id)}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon />
            {!isCollapsed && item.label}
          </div>
        ))}
      </nav>

      <nav className="nav-section">
        {!isCollapsed && <div className="nav-label">Analytics</div>}
        {navItems.analytics.map(item => (
          <div
            key={item.id}
            className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item.id)}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon />
            {!isCollapsed && item.label}
          </div>
        ))}
      </nav>

      <nav className="nav-section">
        {!isCollapsed && <div className="nav-label">{t.reports}</div>}
        {navItems.reportes.map(item => (
          <div
            key={item.id}
            className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item.id)}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon />
            {!isCollapsed && item.label}
          </div>
        ))}
      </nav>

      {/* Sprint 4 — account section (currently: billing portal) */}
      <nav className="nav-section">
        {!isCollapsed && <div className="nav-label">{t.account}</div>}
        {navItems.cuenta.map(item => (
          <div
            key={item.id}
            className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item.id)}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon />
            {!isCollapsed && item.label}
          </div>
        ))}
      </nav>

      {/* User Footer */}
      <div className="sidebar-footer">
        <div className={`user-info-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
          <div className="user-avatar">{userInitials}</div>
          {!isCollapsed && (
            <div className="user-details">
              <div className="user-name-sidebar">{user?.fullName || 'Usuario'}</div>
              <div className="user-role">{user?.primaryEmailAddress?.emailAddress || ''}</div>
            </div>
          )}
          <button
            className="logout-btn"
            onClick={() => signOut()}
            title={isCollapsed ? 'Logout' : 'Cerrar sesión'}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
            </svg>
          </button>
        </div>
      </div>
    </aside>
  )
}

// Icons
function DashboardIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  )
}

function DevicesIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
    </svg>
  )
}

function VulnIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>
    </svg>
  )
}

function LockIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
    </svg>
  )
}

function HistoryIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
    </svg>
  )
}

/** Palette icon — used for the tenant branding/identity section. */
function PaletteIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="13.5" cy="6.5" r=".5"/>
      <circle cx="17.5" cy="10.5" r=".5"/>
      <circle cx="8.5" cy="7.5" r=".5"/>
      <circle cx="6.5" cy="12.5" r=".5"/>
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
    </svg>
  )
}

/** Invoice / receipt icon — used for the tenant billing section. */
function BillingIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <path d="M14 2v6h6"/>
      <path d="M16 13H8"/>
      <path d="M16 17H8"/>
      <path d="M10 9H8"/>
    </svg>
  )
}

function PackageIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12"/>
    </svg>
  )
}

function GridIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  )
}

function PuzzleIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.611a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.23 8.77c.24-.24.581-.353.917-.303.515.077.877.528 1.073 1.01a2.5 2.5 0 1 0 3.259-3.259c-.482-.196-.933-.558-1.01-1.073-.05-.336.062-.676.303-.917l1.525-1.525A2.402 2.402 0 0 1 12 1.998c.617 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02Z"/>
    </svg>
  )
}

function UsbIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="10" cy="7" r="1"/><circle cx="4" cy="20" r="1"/><path d="M4.7 19.3 19 5M21 3l-3 1 2 2ZM9.26 7.68 5 12l2 5M10 14l5 2 3.5-3.5M18 12l1-1"/>
    </svg>
  )
}

function AccessIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <path d="m8.59 13.51 6.83 3.98M15.41 6.51l-6.82 3.98"/>
    </svg>
  )
}

function ThreatIntelIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M12 2L2 7l10 5 10-5-10-5Z"/>
      <path d="M2 17l10 5 10-5"/>
      <path d="M2 12l10 5 10-5"/>
    </svg>
  )
}

function ExposureIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/>
      <path d="M2 12h20"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  )
}

function BrandProtectionIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M12 8v4"/>
      <path d="M12 16h.01"/>
    </svg>
  )
}

function FingerprintIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4"/>
      <path d="M5 19.5C5.5 18 6 15 6 12c0-3.5 2.5-6 6-6 2 0 4 1 5 2.5"/>
      <path d="M22 12c0 6-4.5 8-8 11"/>
      <path d="M10 22c1-3 1.5-5 1.5-8 0-2 1-3.5 2.5-3.5s2.5 1.5 2.5 3.5c0 2.5-.5 4-1 6"/>
      <path d="M14 13.5c0 1-.5 2.5-.5 4"/>
    </svg>
  )
}

function DarkWebIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}

